import { IDay, IEngineStatus, ILog, ILogAutoType, ILogOnOff, INavigationThresholds, IShipStatus, IStatus, IStatusType, Log } from "../../Types"

// Which `Status` was in force at a given log entry (#506).
//
// A `Status` spans `[startLogId, endLogId)`: it takes effect at the log it was opened
// at and stops applying at the log that closed it -- that closing entry already shows
// the new state (engine stopped, sails struck). The interval is therefore **half-open**,
// which is what every log view needs and what `Print/LogView` already did for sails.
// It is deliberately not the closed interval of Swift's
// `AnchorTrackClassifier.status(coveringLogId:in:)` (`Status/MonitorStatus.swift`),
// which answers a different question: which anchor session a waypoint was recorded in.
//
// These derivations used to rely on the caller pre-filtering the status list (only
// `DayLogsTimelineView` did), so any view handed the day's full list -- `Print/DaysView`
// -- showed a status that had not started yet. Each derivation now applies the rule
// itself, so a pre-filter is an optimisation, never a correctness requirement.

// A log id of 0 is a not-yet-persisted entry (the Focus view's current log): it is by
// definition the latest, so exactly the still-open statuses apply to it. Its id sorts
// below every real one, so the `startLogId` comparison cannot be used.
export function statusCoversLog(status: IStatus, logId: number): boolean {
  if (status.deletedAt != undefined) {
    return false;
  }
  if (logId <= 0) {
    return status.endLogId == undefined;
  }
  // `startLogId == 0` marks a day-rollover continuation (see `Status.resolveInterval`),
  // which covers every log of the day -- handled by the same comparison.
  return status.startLogId <= logId && (status.endLogId == undefined || logId < status.endLogId);
}

export function statusesCoveringLog(statuses: IStatus[] | undefined, logId: number): IStatus[] {
  return (statuses || []).filter((s) => statusCoversLog(s, logId));
}

// The status in force at `logId` for one named item (an engine or sail id). When a
// status closes and another opens at the same log, only the reopened one covers it.
export function coveringStatus(statuses: IStatus[] | undefined, logId: number, name?: string): IStatus | undefined {
  let latest: IStatus | undefined;
  for (const status of statuses ?? []) {
    if ((name != undefined && status.name != name) || !statusCoversLog(status, logId)) continue;
    // A real start supersedes a rollover continuation, even when its closing
    // update has not arrived yet. IDs break equal-boundary ties independently of
    // HTTP response or websocket arrival order.
    if (!latest || status.startLogId > latest.startLogId
      || (status.startLogId === latest.startLogId && status.id > latest.id)) {
      latest = status;
    }
  }
  return latest;
}

export function shipStatusAtLog(statuses: IStatus[] | undefined, logId: number): IStatus | undefined {
  return coveringStatus(statuses?.filter((status) => status.statusType === IStatusType.ship), logId);
}

type StatusDay = Pick<IDay, "id" | "shipId">;

function dayShipStatuses(day: StatusDay, statuses: IStatus[] | undefined): IStatus[] {
  return (statuses ?? []).filter((status) => status.shipId === day.shipId && status.dayId === day.id
    && status.statusType === IStatusType.ship && status.deletedAt == undefined)
    .sort((a, b) => a.startLogId - b.startLogId || a.id - b.id);
}

// Day summaries describe physical state, not interval membership (#1900).
// Legacy connection/navigation bookkeeping can close a ship interval without
// changing that state. This exception must never be used for individual log rows.
export function shipStatusAtDayEnd(day: StatusDay, statuses: IStatus[] | undefined, logs: ILog[] | undefined): IShipStatus {
  const shipStatuses = dayShipStatuses(day, statuses);
  const latest = shipStatuses[shipStatuses.length - 1];
  const dayLogs = (logs ?? []).filter((log) => log.shipId === day.shipId && log.dayId === day.id
    && log.id > 0 && log.discardedAt == undefined);
  const deletedStarts = new Set((statuses ?? []).filter((status) => status.shipId === day.shipId && status.dayId === day.id
    && status.statusType === IStatusType.ship && status.deletedAt != undefined).map((status) => status.startLogId));
  const boundary = latest ? Math.max(latest.startLogId, latest.endLogId ?? latest.startLogId) : 0;
  const explicit = dayLogs.filter((log) => log.id >= boundary && log.id > (latest?.startLogId ?? 0))
    .filter((log) => !deletedStarts.has(log.id) || shipStatuses.some((status) => status.startLogId === log.id))
    .sort((a, b) => b.id - a.id).map(Log.parse)
    .find((log) => log.dataParsed.shipStatus != undefined);
  if (explicit?.dataParsed.shipStatus != undefined) {
    return explicit.dataParsed.shipStatus;
  }
  if (!latest) {
    return IShipStatus.none;
  }
  if (latest.endLogId == undefined) {
    return latest.value;
  }
  if (latest.endLogId <= latest.startLogId || latest.endLogId <= 0) {
    return IShipStatus.none;
  }
  const closing = dayLogs.find((log) => log.id === latest.endLogId);
  if (!closing) {
    return IShipStatus.none;
  }
  const metadata = Log.parse(closing).dataParsed;
  const technical = (metadata.type === ILogAutoType.nmeaConnection
    && (metadata.nmeaConnection === ILogOnOff.on || metadata.nmeaConnection === ILogOnOff.off))
  || (metadata.type === ILogAutoType.navigationStatus && metadata.navigationStatus === ILogOnOff.off);
  return technical ? latest.value : IShipStatus.none;
}

export function dayShipStatusSummary(day: StatusDay, statuses: IStatus[] | undefined, logs: ILog[] | undefined): {
  dayStatus: IShipStatus;
  overnightStatus: IShipStatus;
} {
  const shipStatuses = dayShipStatuses(day, statuses);
  const dayStatus = shipStatuses.some((status) => status.value === IShipStatus.inmotion)
    ? IShipStatus.inmotion : shipStatuses[0]?.value ?? IShipStatus.none;
  return {
    dayStatus,
    overnightStatus: dayStatus === IShipStatus.inmotion ? shipStatusAtDayEnd(day, statuses, logs) : IShipStatus.none,
  };
}

// Reefing is recorded as two statuses at the same log: the old stage closes there and the
// new one opens. The sail itself never came down, so showing the closing stage would read
// as if it had been struck ("Genua geborgen") right next to "Genua - Reff 2 gesetzt". The
// stage that was replaced is therefore hidden and only the new one is shown (#1288).
//
// The rule keys on the sail id alone, so it fires exactly when the same sail is up before
// *and* after the log. Swapping the genoa for the jib closes one name and opens another --
// a genuine strike, which stays visible.
export function sailStageReplacedAt(status: IStatus, statuses: IStatus[] | undefined, logId: number): boolean {
  if (status.statusType != IStatusType.sails || status.endLogId != logId) {
    return false;
  }
  // A replacement both opens here and is still up afterwards. `endLogId != logId` also
  // excludes `status` itself without relying on object identity.
  return (statuses || []).some((s) => s.statusType == IStatusType.sails && s.name == status.name && s.startLogId == logId && s.endLogId != logId);
}

export function visibleSailStatuses(statuses: IStatus[] | undefined, logId: number): IStatus[] {
  return (statuses || []).filter((s) => !sailStageReplacedAt(s, statuses, logId));
}

// Whether the manual ship-status mode (#511) is running at this log: navigation makes
// no automatic ship-status changes at all while it is, and the LogButton's manual-status
// action reads as ON and switches it OFF again.
//
// An OPEN `.manualShipStatus` status IS the on-signal (closing it via `endLogId` the
// off-signal), so this is the ordinary covering-status rule -- no expiry check: expiry
// belongs to navigation, which closes the status on its own tick.
export function manualShipStatusActive(statuses: IStatus[] | undefined, logId: number): boolean {
  return (statuses || []).some((s) => s.statusType == IStatusType.manualShipStatus && statusCoversLog(s, logId));
}

// The single engine on/off rule every log view shares: an engine runs when the engine
// status covering this log is open **or** the log's own rpm reading is above the ship's
// engine-off threshold.
//
// The two sources are OR-ed, never ranked. A zero rpm reading must not override an open
// status: an engine started by hand (or by a source that reports no rpm at all) leaves
// `engineRpms` sitting at the zeros the log was seeded with, and letting that win
// rendered every later entry with stopped-engine icons even though the engine was never
// stopped and no stop entry exists anywhere in the log. Stopping an engine always closes
// its status, so "off" is the absence of a covering status, not a zero rpm.
//
// The rpm fallback is measured against `engineStop` rather than against zero (#1819
// testing): navigation stops an engine below that threshold, so a residual reading under
// it -- 100 rpm on a ship whose engine-off threshold is 100 -- used to keep drawing a
// running engine on every entry after the "Stopped engine" one. A reading exactly AT the
// threshold counts as stopped here, which is deliberately one step more conservative
// than navigation's strict `rpm < engineStop`: this is the fallback for a log no status
// covers at all, and an engine sitting on its own off threshold with nothing declaring
// it running is far more likely winding down than under way. With no thresholds
// available the rule stays the historic `rpm > 0`.
export function engineRunningAtLog(engineRpms: number[] | undefined, n: number, status: IStatus | undefined, thresholds?: INavigationThresholds): boolean {
  if (status != undefined) {
    return true;
  }
  const rpm = engineRpms?.[n];
  if (rpm == undefined || rpm <= 0) {
    return false;
  }
  return thresholds?.engineStop == undefined || rpm > thresholds.engineStop;
}

// The engine interval a run actually began at (#1819 testing).
//
// Since #619 an idle crossing splits one engine run into several consecutive intervals:
// the running one closes at the crossing log and an idle one opens there. The interval
// that closes at a stop entry is therefore only the *last* segment, which made
// "Stopped engine after 0:02 (0.2 nm)" report the final idle stretch rather than the
// whole run. Walking back over intervals that meet end-to-end for the same engine
// recovers the log the engine was actually started at. Callers must provide the
// full day's history, not only intervals touching the shutdown log.
//
// The walk stops at a `startLogId` of 0 (a day-rollover continuation, see
// `Status.resolveInterval`), which has no start log of its own, and is bounded by the
// number of statuses so a malformed chain can't loop forever.
export function engineRunStartStatus(statuses: IStatus[] | undefined, status: IStatus | undefined): IStatus | undefined {
  if (status == undefined || status.deletedAt != undefined) {
    return undefined;
  }
  const all = (statuses || []).filter((s) => s.statusType == IStatusType.engine
    && s.deletedAt == undefined && s.shipId == status.shipId && s.dayId == status.dayId
    && s.item == status.item && s.name == status.name);
  let current = status;
  for (let i = 0; i < all.length; i++) {
    if (current.startLogId <= 0) {
      return current;
    }
    const previous = all.find((s) => s.id != current.id && s.endLogId == current.startLogId);
    if (previous == undefined) {
      return current;
    }
    current = previous;
  }
  return current;
}

// `NavigationThresholds.engineIdleActive` (`LogbookFoundation/Navigation.swift`): the
// flag alone is not enough, the three RPM thresholds have to be ordered.
export function engineIdleDetectionActive(thresholds: INavigationThresholds | undefined): boolean {
  if (!thresholds?.engineIdleRpmEnabled) {
    return false;
  }
  return thresholds.engineStop < thresholds.engineStart && thresholds.engineStart < thresholds.engineIdleRpm;
}

// The companion idle rule (#619, generalised in #1819): a running engine is idling when
// the status covering this log says so, and -- for a log with no covering status at all,
// where `engineRunningAtLog` fell back to the rpm reading -- when that reading is below
// the ship's idle threshold.
//
// Callers must gate this on `engineRunningAtLog`: a stopped engine is neither running
// nor idling, and its last rpm reading says nothing about how it ran.
export function engineIdleAtLog(engineRpms: number[] | undefined, n: number, status: IStatus | undefined, thresholds?: INavigationThresholds): boolean {
  if (status != undefined) {
    return status.value == IEngineStatus.idle;
  }
  if (!engineIdleDetectionActive(thresholds)) {
    return false;
  }
  const rpm = engineRpms?.[n];
  return rpm != undefined && rpm < thresholds!.engineIdleRpm;
}
